import { InputText } from "primereact/inputtext";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { FormFieldsPanel } from "../../../../components/form-fields-panel/form-fields-panel";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { IClient } from "../Client.model";
import { useClientForm } from "./ClientForm.hooks";
import classes from "./ClientForm.module.scss";
import { classNames as cx } from "primereact/utils";
import { Button } from "primereact/button";
import { CustomerCategoryEnum } from "../../SalesLead/Lead.model";
import { If } from "../../../../components/If/If";
import { Dropdown } from "primereact/dropdown";
import { toDropdownLabels } from "../../../Employee/EmployeeForm/helpers";
import { countries } from "../../../../helpers/states";
import { useUSAStates } from "../../../../helpers/helperHooks";
import { EmailChangeConfirmDialog } from "../../../../components/EmailChangeConfirmDialog/EmailChangeConfirmDialog";

type ClientFormProps = {
  onSubmit: (props: Omit<IClient, "clientId">) => void;
  clientId?: string;
  isPending: boolean;
  isEditForm?: boolean;
};

export const ClientForm = ({
  onSubmit,
  clientId,
  isPending,
  isEditForm = false,
}: ClientFormProps) => {
  const navigate = useNavigate();

  const { hookForm, cities } = useClientForm(clientId);

  const states = useUSAStates();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = hookForm;

  const [pendingData, setPendingData] = useState<any | null>(null);

  const submitWithEmailGuard = (data: any) => {
    if (isEditForm && dirtyFields?.email) {
      setPendingData(data);
      return;
    }
    onSubmit(data);
  };

  const [customerCategory] = watch(["customerCategory"]);

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };  

  return (
    <form onSubmit={handleSubmit(submitWithEmailGuard)} className={classes.form}>
      <div className={classes.fieldsContainer}>
        <FormFieldsPanel label="General information">
          <div className={classes.fieldsGroupContent}>
            <LabelValue label="Title">
              <Controller
                name="title"
                render={({ field }) => (
                  <InputText
                    {...field}
                    className={cx({ "p-invalid": errors.contactName })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("title")}
            </LabelValue>
            {customerCategory ? (
              <If
                condition={customerCategory === CustomerCategoryEnum.CORPORATE}
              >
                <>
                  <LabelValue label="Company name">
                    <Controller
                      name="companyName"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          placeholder="Client name"
                          className={cx({ "p-invalid": errors.companyName })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("companyName")}
                  </LabelValue>
                  <LabelValue label="Contact name">
                    <Controller
                      name="contactName"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          placeholder="Client name"
                          className={cx({ "p-invalid": errors.contactName })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("contactName")}
                  </LabelValue>
                </>
                <>
                  <LabelValue label="First name">
                    <Controller
                      name="name"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          placeholder="First name"
                          className={cx({ "p-invalid": errors.name })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("name")}
                  </LabelValue>
                  <LabelValue label="Last name">
                    <Controller
                      name="surname"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          placeholder="Last name"
                          className={cx({ "p-invalid": errors.surname })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("surname")}
                  </LabelValue>
                </>
              </If>
            ) : null}
            {/* <LabelValue label="Display name as">
              <Controller
                name="displayNameAs"
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Display name as"
                    className={cx({ "p-invalid": errors.displayNameAs })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("displayNameAs")}
            </LabelValue> */}
            <LabelValue label="Email">
              <Controller
                name="email"
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Email"
                    className={cx({ "p-invalid": errors.email })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("email")}
            </LabelValue>
            <LabelValue label="Phone">
              <Controller
                name="phone"
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Phone"
                    className={cx({ "p-invalid": errors.phone })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("phone")}
            </LabelValue>
            <LabelValue label="Mobile">
              <Controller
                name="mobile"
                render={({ field }) => (
                  <InputText
                    {...field}
                    tabIndex={14}
                    placeholder="Mobile"
                    className={cx({ "p-invalid": errors.mobile })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("mobile")}
            </LabelValue>
            <LabelValue label="Fax">
              <Controller
                name="fax"
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Fax"
                    className={cx({ "p-invalid": errors.fax })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("fax")}
            </LabelValue>
            <LabelValue label="Website">
              <Controller
                name="website"
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Website"
                    className={cx({ "p-invalid": errors.website })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("website")}
            </LabelValue>
            <LabelValue label="Address">
              <Controller
                name="address"
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Address"
                    className={cx({ "p-invalid": errors.address })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("address")}
            </LabelValue>
            <LabelValue label="Country">
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    appendTo="self"
                    id={field.name}
                    tabIndex={7}
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={toDropdownLabels(countries)}
                    placeholder="Country"
                    style={{ width: "207px" }}
                    className={cx({
                      "p-invalid": errors.country,
                    })}
                  />
                )}
              />
              {getFormErrorMessage("country")}
            </LabelValue>
            <LabelValue label="State">
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    appendTo="self"
                    id={field.name}
                    tabIndex={7}
                    value={field.value}
                    onChange={(e) => {
                      setValue("city", "");
                      field.onChange(e.value);
                    }}
                    options={states}
                    placeholder="State"
                    style={{ width: "207px" }}
                    className={cx({
                      "p-invalid": errors.state,
                    })}
                  />
                )}
              />
              {getFormErrorMessage("state")}
            </LabelValue>
            <LabelValue label="City">
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    appendTo="self"
                    id={field.name}
                    tabIndex={8}
                    value={field.value}
                    disabled={!cities?.length}
                    onChange={(e) => field.onChange(e.value)}
                    options={cities}
                    placeholder="City"
                    style={{ width: "207px" }}
                    className={cx({
                      "p-invalid": errors.city,
                    })}
                  />
                )}
              />
              {getFormErrorMessage("city")}
            </LabelValue>
            <LabelValue label="ZIP">
              <Controller
                name="ZIP"
                render={({ field }) => (
                  <InputText
                    {...field}
                    keyfilter="int"
                    tabIndex={9}
                    placeholder="ZIP"
                    className={cx({ "p-invalid": errors.ZIP })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("ZIP")}
            </LabelValue>
          </div>
        </FormFieldsPanel>
      </div>
      <div className={classes.actions}>
        <Button
          label="Cancel"
          type="reset"
          className="p-button-outlined p-button-secondary"
          onClick={() => navigate(-1)}
        />
        <Button
          label={clientId ? "Update" : "Save"}
          type="submit"
          disabled={isPending}
        />
      </div>
      <EmailChangeConfirmDialog
        visible={pendingData !== null}
        newEmail={pendingData?.email ?? ""}
        onCancel={() => setPendingData(null)}
        onConfirm={(notify) => {
          onSubmit({ ...pendingData, notifyEmailChange: notify });
          setPendingData(null);
        }}
      />
    </form>
  );
};
